import React, { useEffect, useRef } from 'react';

import { NounKeys } from '../../../common/constants/nouns';
import {
	all3pProductKeys,
	type ProductKeys3P,
	products1P,
} from '../../../common/constants/products';
import { type ThirdPartyConfigsWithAPIResponse } from '../../../common/constants/schemas/3p-config';
import {
	onSearchPageFiltersViewed,
	type PixelVisibleApp,
} from '../../../common/utils/events/search-page-filters-viewed';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../controllers/analytics';

const FILTER_ALL_TESTID = 'filter-all';
const FILTER_GROUP_TESTID_PREFIX = 'filter-group-';
const FILTER_TESTID_PREFIX = 'filter-';

/**
 * Strictly enforce which app filter keys we will emit
 * into the analytics payload. Anything else parsed from a `data-testid`
 * is rejected, so an unrelated descendant testid like `filter-confluence-active`
 * (a checkbox / icon inside a filter row) cannot leak a spurious
 * `confluence-active` appKey into the analytics payload.
 *
 * 3P keys are included from the static `all3pProductKeys` list (not only
 * the runtime `thirdPartyConfigs` map) because some 3P apps — e.g. Loom —
 * don't require OAuth and therefore aren't added to `thirdPartyConfigs`,
 * which would otherwise silently drop them from the visible-apps payload.
 */
const STATIC_KNOWN_APP_KEYS: ReadonlySet<string> = new Set<string>([
	'all',
	...Object.keys(products1P),
	...all3pProductKeys,
	...Object.values(NounKeys),
]);

/**
 * Build the set of known app keys we are willing to emit. 
 */
const buildKnownAppKeySet = (
	thirdPartyConfigs:
		| Partial<Record<ProductKeys3P, ThirdPartyConfigsWithAPIResponse>>
		| undefined,
): Set<string> => {
	if (!thirdPartyConfigs) {
		return new Set<string>(STATIC_KNOWN_APP_KEYS);
	}
	const known = new Set<string>(STATIC_KNOWN_APP_KEYS);
	Object.keys(thirdPartyConfigs).forEach((key) => known.add(key));
	return known;
};

const parseAppKeyFromTestId = (
	testId: string,
	knownAppKeys: Set<string>,
): string | undefined => {
	if (testId === FILTER_ALL_TESTID) {
		return 'all';
	}
	let candidate: string | undefined;
	if (testId.startsWith(FILTER_GROUP_TESTID_PREFIX)) {
		candidate = testId.slice(FILTER_GROUP_TESTID_PREFIX.length) || undefined;
	} else if (testId.startsWith(FILTER_TESTID_PREFIX)) {
		candidate = testId.slice(FILTER_TESTID_PREFIX.length) || undefined;
	}
	if (!candidate) {
		return undefined;
	}
	return knownAppKeys.has(candidate) ? candidate : undefined;
};

const isElementPixelVisible = (element: Element): boolean => {
	if (!(element instanceof HTMLElement)) {
		return false;
	}
	// `closest` walks ancestors in one shot for the `hidden` attribute
	// (rejects collapsed accordions / closed popups that toggle it on a
	// wrapping element). `aria-hidden="true"` is intentionally excluded:
	// it is an accessibility hint for screen readers, not a visual-visibility
	// signal — elements can be aria-hidden while remaining fully visible,
	// and ADS modals do not set it on background elements.
	if (element.closest('[hidden]')) {
		return false;
	}
	if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
		return true;
	}
	// `visibility` is an inherited CSS property, so `getComputedStyle` on the
	// element itself already reflects any ancestor's `visibility: hidden`.
	// `display` is NOT inherited — an ancestor with `display: none` must be
	// detected directly, so we walk ancestors for that signal only.
	// `getBoundingClientRect`/`offsetParent` are intentionally NOT used:
	// they are unreliable in jsdom (always zero / null).
	const cs = window.getComputedStyle(element);
	if (cs.display === 'none' || cs.visibility === 'hidden') {
		return false;
	}
	for (let cursor = element.parentElement; cursor; cursor = cursor.parentElement) {
		if (window.getComputedStyle(cursor).display === 'none') {
			return false;
		}
	}
	return true;
};

const computeVisibleAppsFromRail = (
	railEl: HTMLElement,
	thirdPartyConfigs: Partial<Record<ProductKeys3P, ThirdPartyConfigsWithAPIResponse>> | undefined,
): PixelVisibleApp[] => {
	const knownAppKeys = buildKnownAppKeySet(thirdPartyConfigs);
	const candidates = railEl.querySelectorAll<HTMLElement>('[data-testid]');
	const seen = new Set<string>();
	const result: PixelVisibleApp[] = [];
	candidates.forEach((el) => {
		const testId = el.getAttribute('data-testid');
		if (!testId) {
			return;
		}
		const appKey = parseAppKeyFromTestId(testId, knownAppKeys);
		if (!appKey || seen.has(appKey)) {
			return;
		}
		if (!isElementPixelVisible(el)) {
			return;
		}
		seen.add(appKey);
		const userNeedsOAuth = (thirdPartyConfigs as Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>)?.[appKey]?.userNeedsOAuth;
		const isAuthenticated = appKey === 'all' ? null : !userNeedsOAuth;
		result.push({ appKey, isAuthenticated });
	});
	return result;
};

type Props = {
	railRef: React.RefObject<HTMLElement>;
	thirdPartyConfigs:
		| Partial<Record<ProductKeys3P, ThirdPartyConfigsWithAPIResponse>>
		| undefined;
};

/**
 * Fires `viewed searchPageFilters` exactly once when the right rail mounts
 * in non-lite mode. No re-fires on subsequent DOM mutations,
 * no re-fires when `thirdPartyConfigs` changes — the payload snapshot taken
 * at the first measurement is treated as authoritative for
 * the lifetime of this mount.
 *
 * SPA navigation away → back unmounts and remounts this component, which
 * resets the per-instance ref guard and produces a fresh fire on the new
 * visit.
 */
export const FireSearchPageFiltersViewedFromRail = ({
	railRef,
	thirdPartyConfigs,
}: Props): null => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const fireRef = useRef(fireAnalyticsEvent);
	useEffect(() => {
		fireRef.current = fireAnalyticsEvent;
	}, [fireAnalyticsEvent]);

	const attributesRef = useRef({ commonAttributes, nonPrivacySafeAttributes });
	useEffect(() => {
		attributesRef.current = { commonAttributes, nonPrivacySafeAttributes };
	}, [commonAttributes, nonPrivacySafeAttributes]);

	const thirdPartyConfigsRef = useRef(thirdPartyConfigs);
	useEffect(() => {
		thirdPartyConfigsRef.current = thirdPartyConfigs;
	}, [thirdPartyConfigs]);

	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (hasFiredRef.current) {
			return;
		}
		// Debounce the initial fire so apps in the right rail have time to stabilise
		// before we snapshot which ones are pixel-visible.
		const timeoutId = setTimeout(() => {
			if (hasFiredRef.current) {
				return;
			}
			const railEl = railRef.current;
			if (!railEl) {
				return;
			}
			const visibleApps = computeVisibleAppsFromRail(railEl, thirdPartyConfigsRef.current);
			if (visibleApps.length === 0) {
				return;
			}
			hasFiredRef.current = true;
			fireRef.current(
				onSearchPageFiltersViewed({
					commonAttributes: attributesRef.current.commonAttributes,
					nonPrivacySafeAttributes: attributesRef.current.nonPrivacySafeAttributes,
					visibleApps,
					isLiteMode: false,
				}),
			);
		}, 250);
		return () => clearTimeout(timeoutId);
	}, [railRef]);

	return null;
};
